import { usePostRobot } from '@lib/sdk/utils/postrobot'
import type {
  UiMessageEnvelope,
  UiMeetingCanceledPayload,
  UiCanvasUpdatedPayload,
  UiSectionListUpdatedPayload,
  UiAppSetDataPayload,
  UiAppUpdateDataPayload,
  UiBroadcastRequest,
  UiMessagePayloads,
} from './types.ui'
import { UI_API_METHOD_TYPES, UI_MESSAGE_TYPES, UI_NATIVE_MESSAGE_TYPES } from './types.ui'
import * as contentUi from './content.ui'
import * as loadingUi from './loading.ui'
import * as locationUi from './location.ui'
import * as appErrorsUi from './app-errors.ui'
import * as canvasUi from './canvas.ui'
import * as postCallUi from './post-call.ui'
import * as piaUi from './pia.ui'

async function onNative<T extends keyof UiMessagePayloads>(
  type: T,
  handler: (data: UiMessagePayloads[T]) => Promise<void>,
) {
  const messageListener = (event: MessageEvent<UiMessageEnvelope<typeof type>>) => {
    if (event.data.type === type) {
      handler(event.data.body)
    }
  }
  window.addEventListener('message', messageListener)

  return () => window.removeEventListener('message', messageListener)
}

async function onPostRobot<T extends keyof UiMessagePayloads>(
  type: T,
  handler: (data: UiMessagePayloads[T]) => Promise<void>,
) {
  const pr = await usePostRobot()
  const listener = pr.on(type, (e) => handler(e.data))

  // return the cleanup function
  return listener.cancel
}

/**
 * Subscribe to a given event type by its key.
 *
 * @example
 * const unsubscribeFromSectionListUpdate = useUi().on('ui_section_list_updated', (event: object) => {
 *   // handle event
 * })
 *
 * // later
 * unsubscribeFromSectionListUpdate.then(unsubscribe => unsubscribe())
 *
 * @param type The event type/key to subscribe to.
 * @param handler The handler function to call when the event is emitted.
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function on<T extends keyof UiMessagePayloads>(type: T, handler: (data: UiMessagePayloads[T]) => Promise<void>) {
  if (UI_NATIVE_MESSAGE_TYPES.includes(type as (typeof UI_NATIVE_MESSAGE_TYPES)[number])) {
    return onNative(type, handler)
  }

  return onPostRobot(type, handler)
}

/**
 * Subscribe to the meeting canceled event.
 *
 * @example
 * const unsubscribeFromMeetingCanceled = useUi().onMeetingCanceled((event: {
 *   accountId?: string;
 *   eventId?: string;
 * }) => {
 *   // handle event
 * })
 *
 * // later
 * unsubscribeFromMeetingCanceled.then(cancel => cancel())
 *
 * @param handler
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function onMeetingCanceled(handler: (data: UiMeetingCanceledPayload) => Promise<void>) {
  return on(UI_MESSAGE_TYPES.UI_MEETING_CANCELED, handler)
}

/**
 * Subscribe to the canvas updated event. It is triggered when the current canvas of the app is updated.
 *
 * @example
 * const unsubscribePromise = useUi().onCanvasUpdated((event: { canvas }) => {
 *   // handle event
 * })
 *
 * // later
 * unsubscribePromise.then(unsubscribe => unsubscribe())
 *
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function onCanvasUpdated(handler: (data: UiCanvasUpdatedPayload) => Promise<void>) {
  return on(UI_MESSAGE_TYPES.UI_CANVAS_UPDATED, handler)
}

/**
 * Subscribe to the section list updated event. It is triggered when the list of sections (products) in the current canvas
 * is updated.
 *
 * @example
 * const unsubscribePromise = useUi().onSectionListUpdated((event: { canvas }) => {
 *   // handle event
 * })
 *
 * // later
 * unsubscribePromise.then(unsubscribe => unsubscribe())
 *
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function onSectionListUpdated(handler: (data: UiSectionListUpdatedPayload) => Promise<void>) {
  return on(UI_MESSAGE_TYPES.UI_SECTION_LIST_UPDATED, handler)
}

/**
 * Subscribe to the app set data event. It is triggered when the app data is set. This event is triggered when the app is
 * loaded and in certain other cases after loading.
 *
 * This is the main event for the canvas app to receive data from the CatalogIQ instance and know about its environment.
 *
 * @example
 * let user = null
 * let isEditMode = null
 * let activeCanvas = null
 * let isPrintMode = false
 * const unsubscribePromise = useUi().onAppSetData((data) => {
 *   user = data?.user
 *   isEditMode = data?.is_edit_mode
 *   activeCanvas = data?.canvas
 *   isPrintMode = data?.display_mode === 'print' || data?.client === 'dsr'
 *   // ...mode logic...
 * })
 *
 * // later
 * unsubscribePromise.then(unsubscribe => unsubscribe())
 *
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function onAppSetData(handler: (data: UiAppSetDataPayload) => Promise<void>) {
  return on(UI_MESSAGE_TYPES.UI_APP_SET_DATA, handler)
}

/**
 * Subscribe to the app update data event. It is triggered when the user changes from edit mode to view mode or vice versa
 * and when the call is started or ended.
 *
 * @example
 * let inCall = false
 * let isEditMode = false
 * const unsubscribePromise = useUi().onAppUpdateData((data) => {
 *   isEditMode = data?.is_edit_mode
 *   inCall = data?.in_call
 *   // ...mode logic...
 * })
 *
 * // later
 * unsubscribePromise.then(unsubscribe => unsubscribe())
 *
 * @returns A Promise resolving to a cleanup function to unsubscribe from the event.
 */
function onAppUpdateData(handler: (data: UiAppUpdateDataPayload) => Promise<void>) {
  return on(UI_MESSAGE_TYPES.UI_APP_UPDATE_DATA, handler)
}

/**
 * Broadcast event to the UI. Hidden from the documentation.
 *
 * @hidden
 */
async function uiBroadcast(payload: UiBroadcastRequest): Promise<void> {
  const pr = await usePostRobot()
  pr.send(window.parent, UI_API_METHOD_TYPES.UI_BROADCAST, payload)
}

export default {
  on,
  onMeetingCanceled,
  onCanvasUpdated,
  onSectionListUpdated,
  onAppSetData,
  onAppUpdateData,
  uiBroadcast,
  ...contentUi,
  ...loadingUi,
  ...locationUi,
  ...appErrorsUi,
  ...canvasUi,
  ...postCallUi,
  ...piaUi,
}
